import i18next from 'i18next';
import {useCallback, useMemo, useState} from 'react';

import * as GlobalEvent from 'web-app/services/global-event';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';
import generateUUID from 'web-app/util/uuid';
import * as SentryService from 'web-app/services/sentry';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import type * as Types from '../types';
import {useCreateCustomPeriod} from './use-create-custom-period';
import type {AdhocPurchasePricingGroups} from './use-pricing-group-for-adhoc-purchase';
import {useSubmissionData, type SubmissionData} from './use-submission-data';
import {useCreatePlansForChildren} from './use-create-plans-for-children';
import {usePlanTemplates} from './use-plan-templates';
import {useUpdateBillingFrequency} from './use-update-billing-frequency';
import {useValidatePlanBeforeSave} from './use-validate-plan-before-save';
import {useValidateAndEditPlan} from './use-validate-and-edit-plan';
import {transformPlanValidationResultsToMergedPlanValidationResults} from '../transformers/plan-validation-transformers';
import {transformApiPlanInputToApiValidatePlanInput} from '../transformers/api-transformers';

type Submit = (submissionData: SubmissionData) => Promise<void>;

type Args = {
    trackSubmission: null | VoidFunction;
    afterSubmission: Types.AfterSubmission;
    setUpBillingFrequency: (billingFrequencyTypeToBeSetUp: Types.BillingFrequencyToBeSetUp) => void;
    setUpEmptyPlan: () => void;
    state: Types.State;
    siteId: string;
    billingProfiles: Array<Types.BillingProfile>;
    pricingGroups: Array<Types.PricingGroupOption>;
    adhocPurchasePricingGroups: null | AdhocPurchasePricingGroups;
    openAddNewTemplateModal?: VoidFunction;
    handleUpdatePlanValidationErrors: Types.HandleUpdatePlanValidationErrors;
    automaticRuleGroupId: string | null;
    initialPlan?: Types.Plan;
};

type Result = {
    handleSubmit: VoidFunction;
    handleSavePlan: VoidFunction;
    isSavePlanDisabled: boolean;
    isSubmissionLoading: boolean;
};

const useSubmission = ({
    trackSubmission,
    afterSubmission,
    setUpBillingFrequency,
    setUpEmptyPlan,
    state,
    siteId,
    billingProfiles,
    pricingGroups,
    adhocPurchasePricingGroups,
    openAddNewTemplateModal,
    handleUpdatePlanValidationErrors,
    automaticRuleGroupId,
    initialPlan,
}: Args): Result => {
    const {
        billingFrequencyState: {billingFrequencyTypeToBeSetUp},
        tuitionsState: {selectedTuitions},
    } = state;

    const {updateBillingFrequency} = useUpdateBillingFrequency();

    const {createCustomPeriod} = useCreateCustomPeriod();
    const {createPlansForChildren} = useCreatePlansForChildren();
    const {savePlanTemplate} = usePlanTemplates({siteId});
    const {validatePlansForChildren} = useValidatePlanBeforeSave();
    const {validateAndEditPlan} = useValidateAndEditPlan();
    const [isLoading, setLoadingState] = useState(false);

    const submissionData = useSubmissionData({
        generateUUID,
        siteId,
        state,
        adhocPurchasePricingGroups,
        pricingGroupOptions: pricingGroups,
        billingProfiles,
        initialPlanTuitions: initialPlan?.tuitions,
    });

    const isEmptyPlan = submissionData.type === 'data' ? submissionData.isEmptyPlan : false;

    const submit: Submit = useCallback(
        async submissionData => {
            const automaticInvoicingPromises = hasValue(submissionData.automaticInvoicingConfigs)
                ? submissionData.automaticInvoicingConfigs.map(config => {
                      return updateBillingFrequency(config);
                  })
                : [];
            const customStartPeriodPromise = hasValue(submissionData.customStartPeriod)
                ? createCustomPeriod({
                      type: 'start',
                      customPeriod: submissionData.customStartPeriod,
                      billingProfiles,
                  })
                : null;

            const planPromises = createPlansForChildren({
                tuitionsWithOverriddenPrice: submissionData.tuitionsWithOverriddenPrice,
                billingProfiles,
                selectedTuitions,
                plans: submissionData.plans,
            });

            const customEndPeriodPromise = hasValue(submissionData.customEndPeriod)
                ? createCustomPeriod({
                      type: 'end',
                      customPeriod: submissionData.customEndPeriod,
                      billingProfiles,
                  })
                : null;

            const templatePromise = hasValue(submissionData.template)
                ? savePlanTemplate(submissionData.template).then(() =>
                      GlobalEvent.updateSuccess(needsTranslations('SimplifiedUsBilling', 'Template created')),
                  )
                : null;

            return returnVoid(
                Promise.all(
                    [
                        ...automaticInvoicingPromises,
                        customStartPeriodPromise,
                        planPromises,
                        customEndPeriodPromise,
                        templatePromise,
                    ].filter(hasValue),
                ),
            );
        },
        [
            selectedTuitions,
            createCustomPeriod,
            billingProfiles,
            createPlansForChildren,
            savePlanTemplate,
            updateBillingFrequency,
        ],
    );

    const handleSubmit = useCallback(() => {
        setLoadingState(true);
        if (submissionData.type === 'error') {
            GlobalEvent.updateError(i18next.t('somethingWentWrong'));
            return;
        }

        if (state.view === 'edit' && hasValue(initialPlan?.id)) {
            validateAndEditPlan({
                plans: submissionData.plans,
                tuitionsWithOverriddenPrice: submissionData.tuitionsWithOverriddenPrice,
                selectedTuitions,
                siteId,
                automaticRuleGroupId,
                oldPlanId: initialPlan.id,
                isPlanPartiallyBilled: state.isPlanPartiallyBilled,
                billingProfiles,
            }).then(validationResults => {
                if (validationResults.type === 'validation-errors') {
                    handleUpdatePlanValidationErrors(
                        transformPlanValidationResultsToMergedPlanValidationResults(validationResults.errors),
                    );
                    setLoadingState(false);
                    return;
                } else if (validationResults.type === 'edited-plan') {
                    setLoadingState(false);
                    GlobalEvent.updateSuccess(
                        i18next.t('react.simplifiedUsBillingPlan.translations.json.templates.plansSuccessfullyCreated'),
                    );
                    afterSubmission(submissionData.childIds);
                    setLoadingState(false);
                    return;
                }
            });
        } else if (state.view === 'create') {
            validatePlansForChildren({
                plans: submissionData.plans({selectedTuitions}).map(transformApiPlanInputToApiValidatePlanInput),
            }).then(validationResults => {
                if (validationResults.some(validationResult => validationResult.type === 'error')) {
                    handleUpdatePlanValidationErrors(validationResults);

                    setLoadingState(false);
                    return;
                } else if (validationResults.some(validationResult => validationResult.type === 'server-error')) {
                    GlobalEvent.updateError(i18next.t('somethingWentWrong'));
                    // Errors are merged together so only 1 error of each type is in the result array
                    validationResults.map(result =>
                        result.type === 'server-error'
                            ? SentryService.captureMessage(`Error validating plan in Chill bill`, {
                                  extra: {error: result.message},
                              })
                            : null,
                    );
                    setLoadingState(false);
                    return;
                } else {
                    submit(submissionData).then(() => {
                        setLoadingState(false);
                        GlobalEvent.updateSuccess(
                            i18next.t(
                                'react.simplifiedUsBillingPlan.translations.json.templates.plansSuccessfullyCreated',
                            ),
                        );
                        afterSubmission(submissionData.childIds);
                    });
                    trackSubmission?.();
                }
            });
        }
    }, [
        afterSubmission,
        automaticRuleGroupId,
        handleUpdatePlanValidationErrors,
        initialPlan,
        selectedTuitions,
        siteId,
        state,
        submissionData,
        submit,
        trackSubmission,
        validateAndEditPlan,
        validatePlansForChildren,
        billingProfiles,
    ]);

    const shouldSetUpBillingFrequency = useMemo(
        () => hasValue(billingFrequencyTypeToBeSetUp),
        [billingFrequencyTypeToBeSetUp],
    );
    const handleSavePlan = useCallback(
        (isAddNewTemplateModalOpen?: boolean) => {
            if (isAddNewTemplateModalOpen && !shouldSetUpBillingFrequency) {
                handleSubmit();
            }
            if (isEmptyPlan) {
                setUpEmptyPlan();
            } else if (isAddNewTemplateModalOpen && shouldSetUpBillingFrequency) {
                setUpBillingFrequency(billingFrequencyTypeToBeSetUp);
            } else if (openAddNewTemplateModal) {
                openAddNewTemplateModal();
            } else if (shouldSetUpBillingFrequency) {
                setUpBillingFrequency(billingFrequencyTypeToBeSetUp);
            } else {
                handleSubmit();
            }
        },
        [
            isEmptyPlan,
            shouldSetUpBillingFrequency,
            setUpEmptyPlan,
            openAddNewTemplateModal,
            setUpBillingFrequency,
            billingFrequencyTypeToBeSetUp,
            handleSubmit,
        ],
    );

    return {
        handleSubmit,
        handleSavePlan,
        isSavePlanDisabled:
            submissionData.type === 'error' ||
            state.childrenErrors.some(error => error.type === 'missing-birthday') ||
            state.childrenErrors.some(error => error.type === 'full-plan-billed'),
        isSubmissionLoading: isLoading,
    };
};

const returnVoid = async <T>(promise: Promise<T>): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return promise.then(() => {});
};

export {useSubmission};
